// ПвП.5 (Фаза 5): минимальный тест-экран Colyseus PvP.
//
// Доступ: /colyseus-test (не в меню, по URL).
//
// Цель: проверить что:
//   • 2 браузера могут подключиться к одной комнате.
//   • Сервер выдаёт каждому свою отфильтрованную руку (@view StateView).
//   • placement / end_turn / hero_attack доходят до движка и стейт
//     синкается обратно.
//
// Это не финальный боевой UI — никакой полировки, только текстовое
// отображение и кнопки. Финальный UI — отдельный экран PvpBattleScreen
// в следующих фазах (после стабилизации серверной части).

import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Room } from '@colyseus/sdk';
import { joinOrCreateMatch, getCurrentRoom, setCurrentRoom } from '../pvp/client';

type Faction = 'deus' | 'syntetics' | 'global' | 'livlife';

const HEROES: Record<Faction, string[]> = {
  deus:      ['deus_kirosa', 'deus_nikosi', 'deus_sina', 'deus_tanaki'],
  syntetics: ['syn_bishor',  'syn_rast',    'syn_skvill','syn_uein'],
  global:    ['global_iv',   'global_li_pul','global_mit','global_rayna'],
  livlife:   ['livlife_ariana','livlife_klod','livlife_lisa','livlife_zayn'],
};

interface Snap {
  phase: string;
  currentTurn: number;
  firstPlayerIdx: number;
  matchOver: boolean;
  winnerSessionId: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  players: any[];
  recentLog: string[];
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function toSnap(state: any): Snap | null {
  if (!state) return null;
  try { return state.toJSON() as Snap; } catch { return null; }
}

const PHASE_RU: Record<string, string> = {
  waiting: 'ожидание',
  mulligan: 'мульти',
  placement: 'размещение',
  reveal: 'раскрытие',
  hero_first: 'герой 1',
  hero_second: 'герой 2',
  end_turn: 'конец хода',
  game_over: 'матч окончен',
};

export function ColyseusTestScreen() {
  const navigate = useNavigate();
  const [nick, setNick]     = useState('Test' + Math.floor(Math.random() * 1000));
  const [faction, setFact]  = useState<Faction>('deus');
  const [heroId, setHero]   = useState<string>(HEROES.deus[0]);
  const [status, setStatus] = useState<string>('');
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [room, setRoom]     = useState<Room<any> | null>(null);
  const [snap, setSnap]     = useState<Snap | null>(null);
  const roomRef             = useRef<Room | null>(null);

  // Placement controls.
  const [selCardId, setSelCardId] = useState<number | null>(null);
  const [selLine, setSelLine]     = useState<0 | 1>(0);
  const [selSlot, setSelSlot]     = useState<0 | 1 | 2 | 3>(0);

  useEffect(() => {
    // ПвП.4b: если в singleton'е уже есть готовая комната (нас перенесли
    // из MatchmakingScreen после матча) — используем её, минуя setup-форму.
    const existing = getCurrentRoom();
    if (existing && !roomRef.current) {
      roomRef.current = existing;
      existing.onStateChange(() => setSnap(toSnap(existing.state)));
      existing.onError((code: number, msg?: string) => {
        // eslint-disable-next-line no-console
        console.error('[colyseus] room error', code, msg);
        setStatus(`Ошибка: ${code} ${msg || ''}`);
      });
      existing.onLeave(() => {
        setStatus('Отключён.');
        setRoom(null);
        setSnap(null);
        roomRef.current = null;
        setCurrentRoom(null);
      });
      setRoom(existing);
      setSnap(toSnap(existing.state));
      setStatus(`Комната ${existing.roomId}, sid=${existing.sessionId}`);
    }
    return () => {
      // Cleanup при unmount — leave + сбросить singleton.
      const r = roomRef.current;
      if (r) {
        setCurrentRoom(null);
        r.leave().catch(() => {});
      }
    };
  }, []);

  const onFaction = (f: Faction) => {
    setFact(f);
    setHero(HEROES[f][0]);
  };

  const myIdx = (): -1 | 0 | 1 => {
    // ПвП.5.7: ВАЖНО — на свежей reflection-decoded стороне снапа поля
    // ArraySchema (players, recentLog) могут быть undefined ДО прихода
    // первого ROOM_STATE. snap.toJSON() пропускает undefined-поля, так
    // что snap.players может не существовать вообще. Без этого guard'а
    // myIdx падает при первом setSnap из onStateChange.
    if (!room || !snap || !Array.isArray(snap.players)) return -1;
    for (let i = 0; i < snap.players.length; i++) {
      if (snap.players[i]?.sessionId === room.sessionId) return i as 0 | 1;
    }
    return -1;
  };

  const join = async () => {
    setStatus('Подключаюсь...');
    try {
      const r = await joinOrCreateMatch({ nick, faction, heroId });
      roomRef.current = r;
      r.onStateChange(() => setSnap(toSnap(r.state)));
      r.onError((code: number, msg?: string) => {
        // eslint-disable-next-line no-console
        console.error('[colyseus] room error', code, msg);
        setStatus(`Ошибка: ${code} ${msg || ''}`);
      });
      r.onLeave(() => {
        setStatus('Отключён.');
        setRoom(null);
        setSnap(null);
        roomRef.current = null;
      });
      setRoom(r);
      setSnap(toSnap(r.state));
      setStatus(`Комната ${r.roomId}, sid=${r.sessionId}`);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      // Дублируем стек в консоль — короткий message из e.message часто
      // не объясняет где именно сломалось.
      // eslint-disable-next-line no-console
      console.error('[colyseus] joinOrCreate failed', e);
      setStatus(`Не удалось: ${e?.message || String(e)} (см. консоль)`);
    }
  };

  const leave = () => {
    roomRef.current?.leave().catch(() => {});
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const send = (type: string, payload?: any) => {
    if (!roomRef.current) return;
    roomRef.current.send(type, payload);
  };

  const doPlacement = () => {
    if (selCardId == null) return;
    send('placement', {
      cardInstanceId: selCardId,
      line: selLine,
      slot: selSlot,
    });
  };

  const idx = myIdx();
  const me  = idx >= 0 ? snap?.players?.[idx] : null;
  const opp = idx >= 0 ? snap?.players?.[1 - idx] : null;

  return (
    <div style={S.root}>
      <div style={S.topBar}>
        <button onClick={() => navigate(-1)} style={S.btnSm}>← Назад</button>
        <div style={S.title}>Colyseus PvP — Test Screen</div>
        <div style={S.statusTxt}>{status}</div>
      </div>

      {!room && (
        <div style={S.setup}>
          <div style={S.row}>
            <label style={S.lbl}>Ник:</label>
            <input
              value={nick}
              onChange={e => setNick(e.target.value)}
              style={S.input}
            />
          </div>
          <div style={S.row}>
            <label style={S.lbl}>Фракция:</label>
            {(Object.keys(HEROES) as Faction[]).map(f => (
              <button
                key={f}
                onClick={() => onFaction(f)}
                style={{ ...S.btnSm, ...(faction === f ? S.btnOn : {}) }}
              >
                {f}
              </button>
            ))}
          </div>
          <div style={S.row}>
            <label style={S.lbl}>Герой:</label>
            <select
              value={heroId}
              onChange={e => setHero(e.target.value)}
              style={S.input}
            >
              {HEROES[faction].map(h => (
                <option key={h} value={h}>{h}</option>
              ))}
            </select>
          </div>
          <div style={S.row}>
            <button onClick={join} style={{ ...S.btn, ...S.btnOn }}>
              ► Найти матч (joinOrCreate)
            </button>
          </div>
          <div style={{ ...S.muted, marginTop: 8 }}>
            Откройте этот URL во втором браузере / окне инкогнито с другой
            фракцией+героем — две сессии встретятся в одной комнате.
          </div>
        </div>
      )}

      {room && snap && (
        <div style={S.match}>
          <div style={S.topInfo}>
            <span>фаза: <b>{PHASE_RU[snap.phase] || snap.phase}</b></span>
            <span>ход: <b>{snap.currentTurn}</b></span>
            <span>я: <b>idx={idx}</b></span>
            <span>первый ход: <b>idx={snap.firstPlayerIdx}</b></span>
            {snap.matchOver && (
              <span style={{ color: '#FF8A1F' }}>
                ✓ МАТЧ ОКОНЧЕН (winner={snap.winnerSessionId || '—'})
              </span>
            )}
            <button onClick={leave} style={{ ...S.btnSm, marginLeft: 'auto' }}>
              Покинуть
            </button>
          </div>

          <div style={S.players}>
            <PlayerView title={`Я (idx=${idx})`} p={me} mine />
            <PlayerView title={`Противник (idx=${idx >= 0 ? 1 - idx : '?'})`} p={opp} />
          </div>

          {/* Hand + placement controls */}
          {me && (
            <div style={S.handBox}>
              <div style={S.h3}>Моя рука ({me.hand?.length ?? 0})</div>
              <div style={S.handList}>
                {(me.hand || []).map((c: any) => (
                  <button
                    key={c.instanceId}
                    onClick={() => setSelCardId(c.instanceId)}
                    style={{
                      ...S.handCard,
                      ...(selCardId === c.instanceId ? S.handCardSel : {}),
                    }}
                  >
                    <div style={{ fontSize: 10, opacity: 0.6 }}>#{c.instanceId} · {c.type}</div>
                    <div style={{ fontWeight: 600 }}>{c.name_ru || c.id}</div>
                    <div style={{ fontSize: 10 }}>
                      atk:{c.baseAtk} arm:{c.baseArmor} hp:{c.baseHp}
                      {c.influenceCost ? ` · влияние ${c.influenceCost}` : ''}
                    </div>
                  </button>
                ))}
                {(!me.hand || me.hand.length === 0) && (
                  <div style={S.muted}>(пусто)</div>
                )}
              </div>

              {/* Placement controls (Ф2) */}
              {snap.phase === 'placement' && (
                <div style={S.placeCtrls}>
                  <span style={S.lbl}>Линия:</span>
                  {([0, 1] as const).map(l => (
                    <button key={l} onClick={() => setSelLine(l)}
                      style={{ ...S.btnSm, ...(selLine === l ? S.btnOn : {}) }}>L{l}</button>
                  ))}
                  <span style={S.lbl}>Слот:</span>
                  {([0, 1, 2, 3] as const).map(s => (
                    <button key={s} onClick={() => setSelSlot(s)}
                      style={{ ...S.btnSm, ...(selSlot === s ? S.btnOn : {}) }}>S{s}</button>
                  ))}
                  <button
                    onClick={doPlacement}
                    disabled={selCardId == null}
                    style={{ ...S.btn, ...S.btnOn, marginLeft: 'auto' }}
                  >
                    ► Положить ({selCardId ?? '—'} → L{selLine}/S{selSlot})
                  </button>
                  {selCardId != null && (
                    <button
                      onClick={() => send('cancel_placement', { cardInstanceId: selCardId })}
                      style={S.btnSm}
                    >
                      Отмена pending
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Phase-specific buttons */}
          <div style={S.actions}>
            {snap.phase === 'placement' && (
              <button onClick={() => send('end_turn')} style={S.btn}>
                ✓ Конец хода
              </button>
            )}
            {(snap.phase === 'hero_first' || snap.phase === 'hero_second') && (
              <>
                <button
                  onClick={() => send('hero_attack', { target: 'hero' })}
                  style={S.btn}
                >
                  ⚔ Атака героем по герою врага
                </button>
                <button
                  onClick={() => send('hero_attack', { target: null })}
                  style={S.btnSm}
                >
                  Пропустить атаку
                </button>
                <div style={{ ...S.muted, width: '100%' }}>
                  Чтобы ударить юнита врага — нажми на него в его сетке выше.
                </div>
              </>
            )}
          </div>

          {/* Log */}
          <div style={S.logBox}>
            <div style={S.h3}>Лог (последние {snap.recentLog?.length || 0})</div>
            <div style={S.logScroll}>
              {(snap.recentLog || []).slice().reverse().map((line, i) => (
                <div key={i} style={S.logLine}>{line}</div>
              ))}
              {(!snap.recentLog || snap.recentLog.length === 0) && (
                <div style={S.muted}>(пусто)</div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // ─── вложенный компонент: панель одного игрока ─────────
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function PlayerView({ title, p, mine }: { title: string; p: any; mine?: boolean }) {
    if (!p) return <div style={S.playerCard}><div style={S.h3}>{title}</div><div style={S.muted}>—</div></div>;
    return (
      <div style={{ ...S.playerCard, ...(mine ? S.playerMine : {}) }}>
        <div style={S.h3}>{title} · {p.nick || '—'} {p.connected ? '●' : '○'}</div>
        <div style={S.muted}>{p.faction || '—'} / hero={p.hero?.id || '—'}</div>
        <div style={S.statsRow}>
          <span>♥ {p.hero?.hp ?? 0}/{p.hero?.baseHp ?? 0}</span>
          <span>⚔ {p.hero?.atk ?? 0}</span>
          <span>🛡 {p.hero?.armor ?? 0}</span>
          <span>● Скр {p.scripts ?? 0}</span>
          <span>◆ Вл {p.influence ?? 0}</span>
          <span>🔥 Ф {p.firewalls ?? 0}</span>
        </div>
        <div style={S.statsRow}>
          <span>рука: {p.handSize ?? 0}</span>
          <span>колода: {p.deckSize ?? 0}</span>
          <span>сброс: {p.discardSize ?? 0}</span>
          <span>ready: {p.ready ? '✓' : '—'}</span>
        </div>
        <Field line={p.line0 || []} idx={0} mine={!!mine} />
        <Field line={p.line1 || []} idx={1} mine={!!mine} />
      </div>
    );
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function Field({ line, idx, mine }: { line: any[]; idx: 0 | 1; mine: boolean }) {
    return (
      <div style={S.lineRow}>
        <div style={S.lineLbl}>L{idx}</div>
        {[0, 1, 2, 3].map(s => {
          const u = line[s];
          const empty = !u || u.instanceId === 0 || !u.alive;
          return (
            <div
              key={s}
              style={{
                ...S.slot,
                ...(empty ? S.slotEmpty : S.slotFilled),
              }}
              onClick={() => {
                if (mine || empty) return;
                // Тап по юниту врага: атака героем (в Ф4/Ф5).
                if (snap?.phase === 'hero_first' || snap?.phase === 'hero_second') {
                  send('hero_attack', { target: u.instanceId });
                }
              }}
            >
              {empty ? '·' : (
                <>
                  <div style={{ fontSize: 9, opacity: 0.7 }}>#{u.instanceId}</div>
                  <div style={{ fontSize: 10, fontWeight: 600 }}>{u.name_ru || u.id}</div>
                  <div style={{ fontSize: 9 }}>
                    {u.atk}/{u.armor}/{u.hp}
                    {u.silenced ? ' 🤐' : ''}
                    {u.sleeperTurnsLeft > 0 ? ` 💤${u.sleeperTurnsLeft}` : ''}
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>
    );
  }
}

// ─── inline styles (минимально, без CSS-модуля) ────────────
const S: Record<string, React.CSSProperties> = {
  root: { padding: 12, color: '#E0E5EF', background: '#0B0E18', minHeight: '100vh', fontFamily: 'monospace', fontSize: 12 },
  topBar: { display: 'flex', gap: 12, alignItems: 'center', marginBottom: 12 },
  title: { fontSize: 14, fontWeight: 700, color: '#3D7DFF' },
  statusTxt: { marginLeft: 'auto', color: '#9AA0AE' },
  setup: { padding: 16, border: '1px solid #2A2F45', borderRadius: 6, maxWidth: 600 },
  row: { display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 },
  lbl: { minWidth: 72, color: '#9AA0AE' },
  input: { padding: '4px 8px', background: '#1A1F2E', color: '#E0E5EF', border: '1px solid #2A2F45', borderRadius: 3, fontFamily: 'monospace' },
  btn: { padding: '8px 14px', background: '#1A1F2E', color: '#E0E5EF', border: '1px solid #3D7DFF', borderRadius: 3, cursor: 'pointer', fontFamily: 'monospace', fontSize: 12 },
  btnSm: { padding: '4px 8px', background: '#1A1F2E', color: '#E0E5EF', border: '1px solid #2A2F45', borderRadius: 3, cursor: 'pointer', fontFamily: 'monospace', fontSize: 11 },
  btnOn: { background: '#3D7DFF', color: '#fff', borderColor: '#3D7DFF' },
  match: { display: 'flex', flexDirection: 'column', gap: 10 },
  topInfo: { display: 'flex', gap: 16, padding: 8, background: '#141826', border: '1px solid #2A2F45', borderRadius: 4 },
  players: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 },
  playerCard: { padding: 8, border: '1px solid #2A2F45', borderRadius: 4, background: '#141826' },
  playerMine: { borderColor: '#3D7DFF' },
  h3: { fontWeight: 700, marginBottom: 4 },
  muted: { color: '#5A6075', fontSize: 11 },
  statsRow: { display: 'flex', gap: 10, marginBottom: 4, fontSize: 11 },
  lineRow: { display: 'flex', gap: 4, marginTop: 4, alignItems: 'stretch' },
  lineLbl: { width: 20, color: '#5A6075', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  slot: { flex: 1, padding: 4, border: '1px solid #2A2F45', borderRadius: 3, minHeight: 40, textAlign: 'center', cursor: 'pointer' },
  slotEmpty: { color: '#3A3E4E', cursor: 'default' },
  slotFilled: { background: '#1A1F2E' },
  handBox: { padding: 8, background: '#141826', border: '1px solid #2A2F45', borderRadius: 4 },
  handList: { display: 'flex', gap: 6, flexWrap: 'wrap' },
  handCard: { padding: 6, minWidth: 110, background: '#1A1F2E', border: '1px solid #2A2F45', borderRadius: 3, cursor: 'pointer', color: '#E0E5EF', fontFamily: 'monospace', textAlign: 'left' as const },
  handCardSel: { borderColor: '#3D7DFF', background: '#1F2840' },
  placeCtrls: { display: 'flex', gap: 6, marginTop: 8, alignItems: 'center', flexWrap: 'wrap' },
  actions: { display: 'flex', gap: 8, flexWrap: 'wrap', padding: 8, background: '#141826', border: '1px solid #2A2F45', borderRadius: 4 },
  logBox: { padding: 8, background: '#141826', border: '1px solid #2A2F45', borderRadius: 4 },
  logScroll: { maxHeight: 200, overflowY: 'auto', fontSize: 11 },
  logLine: { padding: '2px 0', borderBottom: '1px solid #1A1F2E', color: '#9AA0AE' },
};
